<script setup>
/**
 * A SQL prompt onto the live database. No sandbox and no confirmation: it
 * writes to your real data, which is the whole point of having it, so the sheet
 * says so rather than pretending otherwise.
 *
 * Anything that is not a SELECT re-reads the app afterwards, so the screen
 * behind cannot go on showing rows that are no longer there.
 */
import { computed, nextTick, onMounted, ref } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo, formatCombo } from '../../lib/shortcuts'
import { hydrate } from '../../stores'
import { closeSheet, toast } from '../../stores/state'
import { t } from '../../stores/i18n'
import { describeTable, history, isRead, listTables, runSql } from '../../stores/console'

const sql = ref('')
const result = ref(null)
const busy = ref(false)
const tables = ref([])
const box = ref(null)
const past = ref([...history])

onMounted(async () => {
  tables.value = await listTables().catch(() => [])
  nextTick(() => box.value?.focus())
})

const willWrite = computed(() => sql.value.trim() && !isRead(sql.value))

async function go() {
  if (!sql.value.trim() || busy.value) return
  busy.value = true
  const wrote = willWrite.value
  result.value = await runSql(sql.value)
  past.value = [...history]
  // The app is looking at the same tables; let it catch up before anyone reads
  // a stale figure off the screen behind this one.
  if (wrote && !result.value.error) {
    await hydrate()
    toast(t('console.changed', { count: result.value.changed }), 'good')
  }
  busy.value = false
}

const use = (text) => {
  sql.value = text
  nextTick(() => box.value?.focus())
}

/** Clicking a table is nearly always "show me what is in here". */
const peek = (name) => use(`SELECT * FROM ${name} LIMIT 50;`)

async function columnsOf(name) {
  const info = await describeTable(name).catch(() => [])
  use(`-- ${name}: ${info.map((c) => `${c.name} ${c.type}`).join(', ')}\nSELECT * FROM ${name} LIMIT 50;`)
}

/** Cells are data, not markup: long text is clipped, null is said as null. */
const cell = (value) => {
  if (value === null || value === undefined) return 'null'
  const text = String(value)
  return text.length > 120 ? `${text.slice(0, 120)}…` : text
}
</script>

<template>
  <Sheet
    wide
    :title="t('header.console')"
    :subtitle="t('console.subtitle')"
    @close="closeSheet"
  >
    <div class="grid gap-3">
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="text-[11px] font-semibold tracking-wide text-faint uppercase">{{ t('console.tables') }}</span>
        <button
          v-for="table in tables"
          :key="table.name"
          class="chip !py-1 !text-[12px]"
          :title="t('console.columnsOf', { name: table.name })"
          @click="peek(table.name)"
          @dblclick="columnsOf(table.name)"
        >
          {{ table.name }}
        </button>
      </div>

      <div>
        <label class="label" for="sql-box">{{ t('console.statement') }}</label>
        <textarea
          id="sql-box"
          ref="box"
          v-model="sql"
          class="input min-h-[7rem] font-mono !text-[13px]"
          spellcheck="false"
          autocapitalize="off"
          autocomplete="off"
          :placeholder="t('console.placeholder')"
          @keydown.enter.meta.prevent="go"
          @keydown.enter.ctrl.prevent="go"
        />
        <p class="mt-1.5 flex flex-wrap items-center gap-x-2 text-[12px] text-faint">
          <span>{{ t('console.runHint', { keys: formatCombo('mod+enter').join(' ') }) }}</span>
          <span v-if="willWrite" class="text-warn">
            {{ t('console.writes') }}
          </span>
        </p>
      </div>

      <div v-if="past.length">
        <p class="label">{{ t('console.recent') }}</p>
        <div class="scroll-thin max-h-24 overflow-y-auto">
          <button
            v-for="(item, i) in past"
            :key="`${i}-${item}`"
            class="menu-row font-mono !text-[12px]"
            @click="use(item)"
          >
            <span class="truncate">{{ item }}</span>
          </button>
        </div>
      </div>

      <div v-if="result?.error" class="rounded-xl border border-danger/40 bg-danger/10 px-3.5 py-3">
        <p class="flex items-start gap-2 font-mono text-[12.5px] text-danger">
          <Icon name="alert" :size="15" class="mt-0.5 shrink-0" />
          {{ result.error }}
        </p>
      </div>

      <template v-else-if="result">
        <p class="tnum text-[12px] text-muted">
          <template v-if="result.read">
            {{ t('console.rows', { count: result.rows.length }) }}
          </template>
          <template v-else>
            {{ t('console.changed', { count: result.changed }) }}
          </template>
          · {{ t('console.ms', { ms: result.ms }) }}
        </p>

        <!-- Its own scroller: a wide result must not widen the sheet. -->
        <div
          v-if="result.columns.length"
          class="scroll-thin max-h-80 overflow-auto rounded-xl border border-line"
        >
          <table class="w-full border-collapse text-left font-mono text-[12px]">
            <thead class="sticky top-0 bg-raised">
              <tr>
                <th
                  v-for="column in result.columns"
                  :key="column"
                  class="border-b border-line px-2.5 py-1.5 font-semibold whitespace-nowrap"
                >
                  {{ column }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in result.rows" :key="i" class="odd:bg-raised/40">
                <td
                  v-for="column in result.columns"
                  :key="column"
                  class="border-b border-line-soft px-2.5 py-1.5 whitespace-nowrap"
                  :class="row[column] === null && 'text-faint italic'"
                >
                  {{ cell(row[column]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else-if="result.read" class="text-[13px] text-faint">
          {{ t('console.noColumns') }}
        </p>
      </template>
    </div>

    <template #footer>
      <p class="flex-1 text-[12px] text-faint">{{ t('console.footnote') }}</p>
      <button class="btn-soft" @click="closeSheet">{{ t('common.close') }}</button>
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" :disabled="busy || !sql.trim()" @click="go">
        <Icon name="play" :size="16" /> {{ busy ? t('console.running') : t('console.run') }}
        <Kbd combo="mod+enter" />
      </button>
    </template>
  </Sheet>
</template>
